<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, provide, reactive, useId, useSlots } from 'vue';
  import { attributeContextKey } from '../Attribute/Attribute.context';
  import { Button, ButtonColor, ButtonSize, ButtonVariant } from '../Button';
  import {
    alertClass,
    bodyClass,
    contentClass,
    contentColumnsClasses,
    copyRowClass,
    copyTitleClass,
    emptyClass,
    headerActionsClass,
    headerClass,
    headerEndClass,
    headerTopClass,
    leadingClass,
    rootClass,
    subtitleClass,
    surfaceClasses,
    titleClass,
    titleGroupClass,
    trailingClass,
    withDividersClass,
  } from './AttributeList.css';
  import {
    type AttributeListEmits,
    type AttributeListProps,
    type AttributeListSlots,
    AttributeListSurface,
  } from './AttributeList.types';

  const props = withDefaults(defineProps<AttributeListProps>(), {
    surface: AttributeListSurface.None,
    columns: 1,
    titleAs: 'h2',
    withDividers: false,
    empty: false,
    copyable: false,
  });

  const emit = defineEmits<AttributeListEmits>();

  defineSlots<AttributeListSlots>();

  const { t } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-attribute-list-${uid}`,
  );
  const titleId = computed(() => `${resolvedTestId.value}-title`);
  const subtitleId = computed(() => `${resolvedTestId.value}-subtitle`);

  const slots = useSlots();
  const hasTitleSlot = () => !!slots.title;
  const hasSubtitle = () => !!slots.subtitle || !!props.subtitle;
  const hasHeader = () => hasTitleSlot() || !!props.title;

  const resolvedEmptyText = computed(
    () => props.emptyText ?? t('attribute_list.empty'),
  );

  const resolvedCopyLabel = computed(
    () => props.copyLabel ?? t('attribute_list.copy'),
  );

  provide(
    attributeContextKey,
    reactive({ labelColumn: computed(() => props.columns === 1) }),
  );

  const rootTag = () => (hasHeader() ? 'section' : 'div');

  const rootAriaLabel = () => {
    if (props.ariaLabel) {
      return props.ariaLabel;
    }
    return hasTitleSlot() ? props.title : undefined;
  };

  const rootAriaLabelledby = () =>
    hasHeader() && !hasTitleSlot() && !props.ariaLabel
      ? titleId.value
      : undefined;

  const rootAriaDescribedby = () =>
    hasHeader() && hasSubtitle() ? subtitleId.value : undefined;

  const rootRole = () =>
    !hasHeader() && props.ariaLabel ? 'group' : undefined;

  const rootClasses = computed(() => [
    rootClass,
    surfaceClasses[props.surface],
  ]);

  const contentClasses = computed(() => [
    contentClass,
    contentColumnsClasses[props.columns],
    { [withDividersClass]: props.withDividers && props.columns === 1 },
  ]);
</script>

<template>
  <component
    :is="rootTag()"
    :class="rootClasses"
    :role="rootRole()"
    :aria-label="rootAriaLabel()"
    :aria-labelledby="rootAriaLabelledby()"
    :aria-describedby="rootAriaDescribedby()"
    :data-testid="resolvedTestId"
  >
    <div
      v-if="hasHeader()"
      :class="headerClass"
      :data-testid="`${resolvedTestId}-header`"
    >
      <div :class="headerTopClass">
        <div :class="titleGroupClass">
          <span
            v-if="$slots.leading"
            :class="leadingClass"
            :data-testid="`${resolvedTestId}-leading`"
          >
            <slot name="leading" />
          </span>
          <div
            v-if="$slots.title"
            :data-testid="`${resolvedTestId}-title-slot`"
          >
            <slot name="title" />
          </div>
          <component
            :is="titleAs"
            v-else
            :id="titleId"
            :class="titleClass"
            :data-testid="`${resolvedTestId}-title`"
          >
            {{ title }}
          </component>
        </div>
        <div v-if="$slots.actions || $slots.trailing" :class="headerEndClass">
          <div
            v-if="$slots.actions"
            :class="headerActionsClass"
            :data-testid="`${resolvedTestId}-actions`"
          >
            <slot name="actions" />
          </div>
          <div
            v-if="$slots.trailing"
            :class="trailingClass"
            :data-testid="`${resolvedTestId}-trailing`"
          >
            <slot name="trailing" />
          </div>
        </div>
      </div>
      <div
        v-if="hasSubtitle()"
        :id="subtitleId"
        :class="subtitleClass"
        :data-testid="`${resolvedTestId}-subtitle`"
      >
        <slot name="subtitle">{{ subtitle }}</slot>
      </div>
    </div>
    <div :class="bodyClass">
      <div
        v-if="copyable && !empty"
        :class="copyRowClass"
        :data-testid="`${resolvedTestId}-copy-row`"
      >
        <span :class="copyTitleClass">{{ copyTitle }}</span>
        <Button
          :variant="ButtonVariant.Text"
          :color="ButtonColor.Secondary"
          :size="ButtonSize.SM"
          :icon-name="TUNDR_ICONS.COPY"
          :test-id="`${resolvedTestId}-copy`"
          @click="emit('copy')"
        >
          {{ resolvedCopyLabel }}
        </Button>
      </div>
      <p
        v-if="empty"
        :class="emptyClass"
        :data-testid="`${resolvedTestId}-empty`"
      >
        {{ resolvedEmptyText }}
      </p>
      <dl
        v-else
        :class="contentClasses"
        :data-testid="`${resolvedTestId}-content`"
      >
        <slot />
      </dl>
    </div>
    <div
      v-if="$slots.alert"
      :class="alertClass"
      :data-testid="`${resolvedTestId}-alert`"
    >
      <slot name="alert" />
    </div>
  </component>
</template>
